import {FC, useCallback, useEffect, useState} from "react";
import {App, Button, Modal, Space, Typography} from "antd";
import {CheckOutlined, CloseOutlined, StopOutlined, UserOutlined} from "@ant-design/icons";
import AxiosServices from "../../Services/axiosServices";
import {useAuth} from "../../config/AuthContext";
import {useChatEvents} from "../../hooks/useChatEvents";

const {Text} = Typography;

type PendingRequest = {
    idt: number;
    initiator_idt: number;
    initiator_username: string;
    createdAt: string;
};

// Renders a modal queue of pending chat requests addressed to the
// connected user. Fetched once at login, plus pushed in real time via
// SSE when a new request comes in.
const ChatRequestNotifier: FC = () => {
    const {isLoggedIn} = useAuth();
    const {message} = App.useApp();
    const [queue, setQueue] = useState<PendingRequest[]>([]);
    const [busyIdt, setBusyIdt] = useState<number | null>(null);

    const fetchPending = useCallback(() => {
        if (!isLoggedIn) {
            setQueue([]);
            return;
        }
        AxiosServices.chatListPendingRequests()
            .then((rows) => setQueue(rows.map((r) => ({
                idt: r.idt,
                initiator_idt: r.initiator_idt,
                initiator_username: r.initiator_username,
                createdAt: r.createdAt,
            }))))
            .catch((err) => {
                console.error('chatListPendingRequests failed', err);
            });
    }, [isLoggedIn]);

    useEffect(() => {
        fetchPending();
    }, [fetchPending]);

    const token = isLoggedIn ? localStorage.getItem('token') : null;
    useChatEvents(token, {
        onRequest: () => fetchPending(),
        onResponse: () => fetchPending(),
    });

    const current = queue[0];
    if (!current) return null;

    const handle = async (action: 'accept' | 'refuse' | 'block') => {
        setBusyIdt(current.idt);
        try {
            if (action === 'accept') await AxiosServices.chatAccept(current.idt);
            else if (action === 'refuse') await AxiosServices.chatRefuse(current.idt);
            else await AxiosServices.chatBlock(current.idt);
            setQueue((q) => q.filter((r) => r.idt !== current.idt));
            const verbs = {accept: 'acceptée', refuse: 'refusée', block: 'bloquée'};
            message.success(`Demande ${verbs[action]}.`);
        } catch (err: any) {
            const status = err?.response?.status;
            const msg = err?.response?.data?.message ?? err?.message ?? 'erreur inconnue';
            message.error(`Échec ${status ?? ''} : ${msg}`);
        } finally {
            setBusyIdt(null);
        }
    };

    return (
        <Modal
            open
            title={
                <Space>
                    <UserOutlined/>
                    <span>Nouvelle demande de discussion</span>
                </Space>
            }
            footer={null}
            closable={false}
            maskClosable={false}
        >
            <Text>
                <Text strong>{current.initiator_username}</Text> souhaite discuter avec toi.
            </Text>
            <div style={{marginTop: 8, color: 'rgba(0,0,0,0.45)', fontSize: 12}}>
                Reçue le {new Date(current.createdAt).toLocaleString()}
            </div>
            <Space direction="vertical" style={{width: '100%', marginTop: 16}}>
                <Button
                    block
                    type="primary"
                    icon={<CheckOutlined/>}
                    onClick={() => handle('accept')}
                    loading={busyIdt === current.idt}
                >
                    Accepter
                </Button>
                <Button
                    block
                    icon={<CloseOutlined/>}
                    onClick={() => handle('refuse')}
                    loading={busyIdt === current.idt}
                >
                    Refuser cette demande (autoriser d'autres tentatives)
                </Button>
                <Button
                    block
                    danger
                    icon={<StopOutlined/>}
                    onClick={() => handle('block')}
                    loading={busyIdt === current.idt}
                >
                    Refuser et bloquer définitivement
                </Button>
            </Space>
            {queue.length > 1 && (
                <div style={{marginTop: 12, fontSize: 12, color: 'rgba(0,0,0,0.55)', textAlign: 'right'}}>
                    {queue.length - 1} autre{queue.length - 1 > 1 ? 's' : ''} demande{queue.length - 1 > 1 ? 's' : ''} en attente.
                </div>
            )}
        </Modal>
    );
};

export default ChatRequestNotifier;
